// File: app/src/utils/capabilityCurves.js
//
// Turns a user's benchmarks for one exercise into a curve (issue #91).
// benchmarkNormalise reads each row on its own; this groups the rows by
// exercise and reconciles them:
//   - lifts: one estimated 1RM from every row (combineOneRm), and the weight
//     for any number of reps from the lifter's own rep curve
//   - timed distances or calories: threshold from critical speed (or power),
//     fitted through two or more efforts on the same machine, plus the best
//     sprint
//   - anything else with one figure (max reps, a hold, watts, metres):
//     progress over time, once there are two or more results
//
// It never projects past the data: too few or inconsistent efforts give no
// curve, and the per-row figures stand alone.
//
// Pure: no React, no I/O.

import { shownWeight, showWeight } from "./units";
import { resolveExercise, normaliseLabelTokens } from "../config/exercises";
import {
  benchmarkAge,
  clock,
  combineOneRm,
  effortToRir,
  isErg,
  liftSet,
  MAX_ESTIMATE_REPS,
  repPercent,
  timeForCalories,
  timeForDistance,
} from "./benchmarkNormalise";

// ─── Grouping ────────────────────────────────────────────

// Known machines, read from the label. Order matters: "Concept2 BikeErg" is
// a bike, not a rower.
const ACTIVITIES = [
  { key: "airbike", label: "Air bike", match: /assault|air\s*bike|echo\s*bike|airdyne/ },
  { key: "bikeerg", label: "Bike", match: /bike|cycl/ },
  { key: "skierg", label: "Ski erg", match: /\bski/ },
  { key: "rower", label: "Rower", match: /\brow|concept\s*2|\bc2\b|\berg\b/ },
  { key: "run", label: "Run", match: /\brun|\bjog|parkrun|treadmill/ },
];

export const activityOf = (label) => {
  const text = String(label || "").toLowerCase();
  return ACTIVITIES.find((a) => a.match.test(text)) || null;
};

// Words that describe the effort, not the machine, so "Sled push 20 m" and
// "Sled push 50 m test" group as "Sled push".
const EFFORT_WORDS = new Set([
  "m", "km", "k", "mi", "mile", "miles", "metre", "metres", "meter", "meters",
  "s", "sec", "secs", "second", "seconds", "min", "mins", "minute", "minutes",
  "cal", "cals", "calorie", "calories", "test", "time", "trial", "tt",
  "sprint", "max", "all", "out", "effort", "piece", "for", "in",
]);
const MEASURE = /^\d+(\.\d+)?(m|km|k|mi|min|mins|s|sec|secs|cal|cals)?$/;

const machineStem = (label) => {
  const words = String(label || "")
    .toLowerCase()
    .replace(/[^a-z0-9.\s]+/g, " ")
    .split(/\s+/)
    .filter((w) => w && !MEASURE.test(w) && !EFFORT_WORDS.has(w));
  if (!words.length) return null;
  const text = words.join(" ");
  return { key: text, label: text.charAt(0).toUpperCase() + text.slice(1) };
};

/**
 * Which curve a row belongs to: { key, label, kind } with kind "lift",
 * "distance", "calories" or "progress" (one figure, tracked over time), or
 * null when it has no usable figure or no recognisable machine.
 */
export const curveGroupOf = (cap) => {
  if (!cap) return null;
  if (liftSet(cap)) {
    const exercise = resolveExercise(cap.label);
    if (exercise) return { key: `lift:${exercise.key}`, label: exercise.name, kind: "lift" };
    const tokens = normaliseLabelTokens(cap.label);
    if (!tokens.length) return null;
    return { key: `lift:${tokens.join(" ")}`, label: String(cap.label).trim(), kind: "lift" };
  }
  const kind = timeForDistance(cap) ? "distance" : timeForCalories(cap) ? "calories" : null;
  if (!kind) return progressGroupOf(cap);
  const machine = activityOf(cap.label) || machineStem(cap.label);
  if (!machine) return null;
  return {
    key: `${kind}:${machine.key}`,
    label: kind === "calories" ? `${machine.label}, calories` : machine.label,
    kind,
  };
};

const KIND_ORDER = { lift: 0, distance: 1, calories: 2, progress: 3 };

/**
 * Benchmarks grouped by exercise for the curves: lifts, then distances, then
 * calories, then progress, each with the most-logged first. Progress needs
 * two results, so a one-off stays only in the main list.
 */
export const benchmarkGroups = (capabilities) => {
  const groups = new Map();
  for (const cap of capabilities || []) {
    const g = curveGroupOf(cap);
    if (!g) continue;
    if (!groups.has(g.key)) groups.set(g.key, { ...g, rows: [] });
    groups.get(g.key).rows.push(cap);
  }
  return [...groups.values()]
    .filter((g) => g.kind !== "progress" || g.rows.length >= 2)
    .sort(
    (a, b) =>
      KIND_ORDER[a.kind] - KIND_ORDER[b.kind] ||
      b.rows.length - a.rows.length ||
      a.label.localeCompare(b.label),
  );
};

// ─── Anything else: progress over time ───────────────────

const PROGRESS_UNITS = new Set(["reps", "sec", "W", "m"]);

const figureOf = (raw) => {
  if (raw == null || raw === "") return null;
  const n = Number(raw);
  return Number.isFinite(n) && n > 0 ? n : null;
};

// A time is a hold (longer is better) when the name says so; any other time
// is for a job done (faster is better). "Hang" alone, not a hang clean.
const HOLD = /hold|plank|\bhang(?!\s*(clean|snatch|power))|\bl[\s-]?sit|wall[\s-]?sit|breath|isometric/;

export const isHold = (label) => HOLD.test(String(label || "").toLowerCase());

/** True when a bigger number is the better result for this row. */
export const higherIsBetter = (cap) => cap.unit !== "sec" || isHold(cap.label);

/** A figure and its unit apart: { value: "22", unit: "reps" }; a time has no unit. */
export const amountParts = (value, unit) => {
  if (unit === "sec") return { value: clock(value), unit: "" };
  const v = Math.round(value * 10) / 10;
  if (unit === "reps") return { value: String(v), unit: v === 1 ? "rep" : "reps" };
  if (unit === "cals") return { value: String(v), unit: "cal" };
  return { value: v.toLocaleString("en-AU"), unit };
};

/** A figure in its unit: "22 reps", "1:45", "310 W", "2,450 m". */
export const formatAmount = (value, unit) => {
  const parts = amountParts(value, unit);
  return parts.unit ? `${parts.value} ${parts.unit}` : parts.value;
};

const progressGroupOf = (cap) => {
  if (!PROGRESS_UNITS.has(cap.unit) || figureOf(cap.value) == null) return null;
  const exercise = resolveExercise(cap.label);
  const tokens = normaliseLabelTokens(cap.label);
  if (!exercise && !tokens.length) return null;
  // "Max burpees in 1:00" and "in 2:00" are different tests.
  const target = figureOf(cap.target_value) != null && cap.target_unit ? cap.target_unit : null;
  const within = target
    ? target === "sec"
      ? `, in ${clock(Number(cap.target_value))}`
      : `, ${formatAmount(Number(cap.target_value), target)}`
    : "";
  return {
    key: `progress:${cap.unit}:${exercise ? exercise.key : tokens.join(" ")}${target ? `:${cap.target_value}${target}` : ""}`,
    label: `${exercise ? exercise.name : String(cap.label).trim()}${within}`,
    kind: "progress",
  };
};

/**
 * A one-figure result's history: each dated result in order, the latest,
 * the best, and the change from the first to the latest (`better` says
 * which way that went). Null when no row has a figure and a date.
 */
export const progressSeries = (rows, now = new Date()) => {
  const points = (rows || [])
    .map((cap) => {
      const value = figureOf(cap.value);
      const date = benchmarkAge(cap, now)?.date;
      if (value == null || !date) return null;
      const rir = effortToRir(cap.effort);
      return { cap, value, date, moderate: rir != null && rir >= 3 };
    })
    .filter(Boolean)
    .sort((a, b) => a.date - b.date || String(a.cap.created_at || "").localeCompare(String(b.cap.created_at || "")));
  if (!points.length) return null;
  const unit = points[0].cap.unit;
  const higher = higherIsBetter(points[0].cap);
  const first = points[0];
  const latest = points[points.length - 1];
  const best = points.reduce((a, p) => ((higher ? p.value > a.value : p.value < a.value) ? p : a));
  const change = points.length > 1 ? latest.value - first.value : null;
  return {
    unit,
    higher,
    points,
    first,
    latest,
    best,
    change,
    better: change ? (change > 0) === higher : null,
  };
};

const sign = (change) => (change > 0 ? "+" : "\u2212");

/** A change as "+4 reps", "−0:15", or "no change". */
export const formatChange = (change, unit) =>
  change ? `${sign(change)}${formatAmount(Math.abs(change), unit)}` : "no change";

/** A change's number alone, signed: "+4", "−0:15", or "0". */
export const changeFigure = (change, unit) =>
  change ? `${sign(change)}${amountParts(Math.abs(change), unit).value}` : "0";

// ─── Lifts: load and reps ────────────────────────────────

/**
 * The lift's curve: the combined 1RM and, from the lifter's own rep curve
 * when there is one, the weight for any number of reps and the reps for any
 * weight. Null when no row gives an estimate.
 */
export const liftCurve = (rows, now = new Date()) => {
  const combined = combineOneRm(rows, now);
  if (!combined) return null;
  const k = combined.curve?.k ?? 1;
  const oneRm = combined.kg;
  const kgAt = (reps) => oneRm * repPercent(reps, k);
  /** Reps to failure at this weight: { reps }, or { over } past the max, or { many } past 20. */
  const repsAt = (kg) => {
    const f = kg / oneRm;
    if (f > 1) return { over: true };
    if (f < repPercent(MAX_ESTIMATE_REPS, k)) return { many: true };
    let lo = 1;
    let hi = MAX_ESTIMATE_REPS;
    for (let i = 0; i < 40; i += 1) {
      const mid = (lo + hi) / 2;
      if (repPercent(mid, k) > f) lo = mid;
      else hi = mid;
    }
    return { reps: lo };
  };
  return { ...combined, oneRm, k, personal: !!combined.curve, kgAt, repsAt };
};

// ─── Distance and calories: critical speed ───────────────

// Critical speed holds for efforts of roughly 2–30 minutes. Shorter ones are
// sprints, which lean on stores the model doesn't describe.
const CS_MIN_SEC = 120;
const CS_MAX_SEC = 1800;
// Two efforts too close in length can't separate speed from reserve.
const MIN_DURATION_SPREAD = 1.5;

/**
 * Fits amount = CS × time + reserve by least squares, where amount is
 * metres (critical speed) or calories (critical power). Null unless both
 * come out positive; a negative reserve means the efforts disagree.
 */
export const fitCriticalSpeed = (points) => {
  if (points.length < 2) return null;
  const secs = points.map((p) => p.sec);
  if (Math.max(...secs) / Math.min(...secs) < MIN_DURATION_SPREAD) return null;
  const n = points.length;
  const meanT = secs.reduce((a, b) => a + b, 0) / n;
  const meanD = points.reduce((a, p) => a + p.m, 0) / n;
  let cov = 0;
  let varT = 0;
  for (const p of points) {
    cov += (p.sec - meanT) * (p.m - meanD);
    varT += (p.sec - meanT) ** 2;
  }
  const cs = cov / varT;
  const dPrime = meanD - cs * meanT;
  if (!(cs > 0) || !(dPrime > 0)) return null;
  return { cs, dPrime };
};

// Timed rows as { sec, m } points, where m is metres or calories. Moderate
// efforts say too little about the limit, so they're marked and left out.
export const effortPoints = (rows, now) =>
  rows
    .map((cap) => {
      const dist = timeForDistance(cap);
      const cals = dist ? null : timeForCalories(cap);
      const sec = dist?.sec ?? cals?.sec;
      const m = dist?.m ?? cals?.cals;
      if (sec == null || m == null) return null;
      const rir = effortToRir(cap.effort);
      return {
        cap,
        sec,
        m,
        moderate: rir != null && rir >= 3,
        firm: rir === 0,
        stale: !!benchmarkAge(cap, now)?.stale,
      };
    })
    .filter(Boolean);

// Best effort per amount, so a repeat test doesn't count twice.
const bestPerAmount = (points) => {
  const best = new Map();
  for (const p of points) {
    const prev = best.get(p.m);
    if (!prev || p.sec < prev.sec) best.set(p.m, p);
  }
  return [...best.values()];
};

/**
 * The machine's curve: the critical-speed fit, the efforts behind it, and
 * the best sprint (amount per second). `amountIn` and `timeFor` answer only
 * inside the span of the fitted efforts. Null with too few usable efforts.
 */
export const workCurve = (rows, now = new Date()) => {
  const points = effortPoints(rows, now);
  const usable = points.filter((p) => !p.moderate);
  const inRange = usable.filter((p) => p.sec >= CS_MIN_SEC && p.sec <= CS_MAX_SEC);
  // Prefer recent efforts; fall back to older ones rather than nothing.
  let used = bestPerAmount(inRange.filter((p) => !p.stale));
  let fit = fitCriticalSpeed(used);
  if (!fit) {
    used = bestPerAmount(inRange);
    fit = fitCriticalSpeed(used);
  }
  if (!fit) return null;
  const sprints = usable.filter((p) => p.sec < CS_MIN_SEC);
  const span = [Math.min(...used.map((p) => p.sec)), Math.max(...used.map((p) => p.sec))];
  const amountIn = (sec) => (sec >= span[0] && sec <= span[1] ? fit.cs * sec + fit.dPrime : null);
  const timeFor = (amount) => {
    const sec = (amount - fit.dPrime) / fit.cs;
    return sec >= span[0] && sec <= span[1] ? sec : null;
  };
  return {
    cs: fit.cs,
    reserve: fit.dPrime,
    points,
    used,
    span,
    sprintRate: sprints.length ? Math.max(...sprints.map((p) => p.m / p.sec)) : null,
    rough: used.some((p) => !p.firm),
    stale: used.some((p) => p.stale),
    amountIn,
    timeFor,
  };
};

// ─── Summaries for the prompt and the list ───────────────

const calPerMin = (perSec) => Math.round(perSec * 60 * 10) / 10;

const summarise = (group, now) => {
  if (group.kind === "lift") {
    const curve = liftCurve(group.rows, now);
    if (!curve || curve.rows < 2) return null;
    const { kgAt, repsAt, ...rest } = curve;
    return { ...rest, kind: "lift", key: group.key, label: group.label };
  }
  if (group.kind === "progress" || group.rows.length < 2) return null;
  const curve = workCurve(group.rows, now);
  if (!curve) return null;
  const threshold = {
    reserve: Math.round(curve.reserve),
    efforts: curve.used.length,
    rough: curve.rough,
    stale: curve.stale,
  };
  if (group.kind === "calories") {
    return {
      kind: "calories",
      key: group.key,
      label: group.label,
      threshold: { ...threshold, calPerMin: calPerMin(curve.cs) },
      sprint: curve.sprintRate != null ? { calPerMin: calPerMin(curve.sprintRate) } : null,
    };
  }
  const erg = group.rows.some((r) => isErg(r.label));
  const per = erg ? 500 : 1000;
  return {
    kind: "endurance",
    key: group.key,
    label: group.label,
    per: erg ? "/500 m" : "/km",
    threshold: { ...threshold, pace: clock(per / curve.cs), reserveM: threshold.reserve },
    sprint: curve.sprintRate != null ? { pace: clock(per / curve.sprintRate) } : null,
  };
};

/** One summary per exercise with two or more benchmarks to combine. */
export const combineBenchmarks = (capabilities, now = new Date()) =>
  benchmarkGroups(capabilities)
    .map((g) => summarise(g, now))
    .filter(Boolean);

/**
 * The headline for one exercise in the curves list: its figure (1RM or
 * threshold) and a detail line, or what's missing for a curve.
 */
export const describeGroup = (group, now = new Date()) => {
  const n = group.rows.length;
  if (group.kind === "lift") {
    const curve = liftCurve(group.rows, now);
    if (!curve) return { figure: null, detail: "No estimate: add the reps you did" };
    const notes = [`from ${curve.rows} result${curve.rows === 1 ? "" : "s"}`];
    if (curve.personal) notes.push("fitted to your max");
    if (curve.conflict) notes.push(`they range ${shownWeight(curve.conflict.low)}–${showWeight(curve.conflict.high)}`);
    return { figure: `≈ ${showWeight(curve.oneRm)} 1RM`, detail: notes.join(" · ") };
  }
  if (group.kind === "progress") {
    const series = progressSeries(group.rows, now);
    if (!series) return { figure: null, detail: `${n} results` };
    const since = series.first.date.toLocaleDateString("en-AU", { month: "short", year: "numeric" });
    return {
      figure: `Best ${formatAmount(series.best.value, series.unit)}`,
      detail: `${n} results · ${formatChange(series.change, series.unit)} since ${since}`,
    };
  }
  const summary = summarise(group, now);
  if (summary) return describeCombined(summary);
  return {
    figure: null,
    detail: `${n} effort${n === 1 ? "" : "s"} · add ${n === 1 ? "another" : "one"} of 2–30 minutes for a curve`,
  };
};

/** How a lifter's reps fall off against the chart, in words, or null. */
export const repCurveLean = (k) => {
  if (k == null) return null;
  if (k < 0.95) return "holds reps better than the standard chart";
  if (k > 1.05) return "loses reps faster than the standard chart";
  return "close to the standard chart";
};

/** A summary in plain words for the Benchmarks list. */
export const describeCombined = (s) => {
  if (s.kind === "lift") {
    const notes = [`from ${s.rows} results`];
    if (s.curve) notes.push("fitted to your max");
    if (s.conflict) notes.push(`they range ${shownWeight(s.conflict.low)}–${showWeight(s.conflict.high)}`);
    if (s.stale) notes.push("over six months old");
    return {
      figure: `≈ ${showWeight(s.kg)} 1RM`,
      detail: notes.join(" · "),
    };
  }
  const t = s.threshold;
  // Non-breaking spaces keep each figure on one line when the row wraps.
  const nb = (text) => text.replace(/ /g, " ");
  const parts =
    s.kind === "calories"
      ? [`Threshold ${nb(`${t.calPerMin} cal/min`)}`].concat(
          s.sprint ? [`sprint ${nb(`${s.sprint.calPerMin} cal/min`)}`] : [],
        )
      : [`Threshold ${nb(`${t.pace} ${s.per}`)}`].concat(
          s.sprint ? [`sprint ${nb(`${s.sprint.pace} ${s.per}`)}`] : [],
        );
  const notes = [`from ${t.efforts} efforts`];
  if (t.rough) notes.push("rough, not all out");
  if (t.stale) notes.push("some over six months old");
  return { figure: parts.join(" · "), detail: notes.join(" · ") };
};
